(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionSelector = '[data-mirthly-motion]';
  const heroSelectors = ['.mh-hero', '.gk-hero', '.product-hero', '.banner', '.slideshow__slide:first-child'];
  const imageSelectors = ['.mh-hero__media', '.gk-gallery', '.product__media-wrapper', '.banner__media', '.hero-media', '.gallery'];
  const copySelectors = ['.mh-hero__copy', '.gk-buy', '.product__info-container', '.banner__content', '.hero-copy', '.buy-box'];
  const sectionSelectors = ['#MainContent > .shopify-section', '.mh main > section', '.gk main > section', 'main > section'];
  const childSelectors = [
    '.mh-section-head', '.gk-section-head', '.section-header', '.title-wrapper-with-link',
    '.mh-featured__copy', '.gk-editorial__copy', '.gk-lifestyle__copy', '.section-copy',
    '.mh-featured__media', '.mh-benefits__media', '.mh-included__media', '.gk-editorial__media',
    '.gk-included__media', '.gk-lifestyle__media', '.image-with-text__media', '.product__media-wrapper',
    '.gk-review', '.review-card', '.card-wrapper', '.product-card-wrapper', '.gk-bundle',
    '.curler-variant', '.mh-why__grid article', '.mh-benefits__grid article', '.gk-faq details', '.faq details'
  ];

  const unique = values => [...new Set(values.filter(Boolean))];

  const setHeroMotion = root => {
    unique(heroSelectors.flatMap(selector => [...root.querySelectorAll(selector)])).forEach(hero => {
      const image = imageSelectors.map(selector => hero.querySelector(selector)).find(Boolean);
      if (image && !image.hasAttribute('data-mirthly-hero')) {
        image.dataset.mirthlyHero = 'image';
        image.style.setProperty('--m-hero-delay', '0ms');
      }

      const copy = copySelectors.map(selector => hero.querySelector(selector)).find(Boolean);
      if (!copy) return;
      [...copy.children].forEach((child, index) => {
        if (child.hasAttribute('data-mirthly-hero')) return;
        child.dataset.mirthlyHero = 'copy';
        child.style.setProperty('--m-hero-delay', `${80 + Math.min(index, 5) * 100}ms`);
      });
    });
  };

  const motionType = element => {
    if (element.matches('figure, picture, [class*="__media"], .gallery, .gk-gallery, .product__media-wrapper')) return 'image';
    if (element.matches('article, details, .card-wrapper, .product-card-wrapper, .gk-bundle, .curler-variant, .gk-review, .review-card')) return 'card';
    return 'text';
  };

  const setScrollMotion = root => {
    const sections = unique(sectionSelectors.flatMap(selector => [...root.querySelectorAll(selector)]));
    sections.forEach(section => {
      // Independent product sections contain a whole page, not one reveal panel.
      if (section.matches('[data-harness], .ev, .gk, .mpp') || section.querySelector('[data-harness], .ev, .gk, .mpp')) return;
      if (section.closest('.mh')) return;
      if (heroSelectors.some(selector => section.matches(selector) || section.querySelector(selector))) return;
      const targets = unique(childSelectors.flatMap(selector => [...section.querySelectorAll(selector)]));
      const visibleTargets = targets.filter(target => !targets.some(parent => parent !== target && parent.contains(target)));
      const finalTargets = visibleTargets.length ? visibleTargets : [section];
      finalTargets.forEach((target, index) => {
        target.dataset.mirthlyMotion = motionType(target);
        target.style.setProperty('--m-motion-delay', `${(index % 5) * 90}ms`);
      });
    });

    unique([...root.querySelectorAll('footer, .footer, .mirthly-footer')]).filter(footer => !footer.closest('.mh')).forEach(footer => {
      footer.dataset.mirthlyMotion = 'text';
    });
  };

  const observeMotion = root => {
    const targets = [...root.querySelectorAll(motionSelector)].filter(target => !target.closest('.mh') && !target.classList.contains('is-motion-visible'));
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach(target => {
        target.classList.add('is-motion-visible', 'is-visible');
        target.style.removeProperty('will-change');
      });
      return;
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-motion-visible', 'is-visible');
        entry.target.addEventListener('transitionend', () => entry.target.style.removeProperty('will-change'), { once: true });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -7% 0px' });

    targets.forEach(target => observer.observe(target));
  };

  const initHeader = root => {
    const headers = [...root.querySelectorAll('.header, .header-wrapper, .shopify-section-header-sticky')];
    if (!headers.length) return;
    let scheduled = false;
    const update = () => {
      headers.forEach(header => header.classList.toggle('mirthly-header-scrolled', window.scrollY > 40));
      scheduled = false;
    };
    update();
    window.addEventListener('scroll', () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    }, { passive: true });
  };

  const initGallery = root => {
    [...root.querySelectorAll('[data-gallery-thumb], .thumbs button')].forEach(thumb => {
      if (thumb.dataset.mirthlyGalleryBound === 'true') return;
      thumb.dataset.mirthlyGalleryBound = 'true';
      thumb.addEventListener('click', () => {
        const gallery = thumb.closest('[data-gallery], .gallery') || root;
        const stage = gallery.querySelector('.gk-gallery__stage, .gallery-main');
        const image = gallery.querySelector('[data-gallery-main], [data-main-image]');
        const target = stage || image;
        if (!target) return;
        target.classList.add('is-switching');
        window.setTimeout(() => target.classList.remove('is-switching'), 210);
      }, { capture: true });
    });
  };

  const initSticky = root => {
    if (!('IntersectionObserver' in window)) return;
    [...root.querySelectorAll('[data-sticky-bar], [data-editorial-sticky], .gk-sticky, .editorial-sticky-atc')].forEach(sticky => {
      if (sticky.dataset.mirthlyStickyBound === 'true') return;
      const scope = sticky.closest('mirthly-curler, mirthly-grooming-kit, mirthly-editorial') || root;
      const source = scope.querySelector('[data-atc], [data-live-add], button[name="add"], .product-form__submit');
      if (!source) return;
      sticky.dataset.mirthlyStickyBound = 'true';
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0));
      }, { threshold: 0 });
      observer.observe(source);
    });
  };

  const initFaq = root => {
    [...root.querySelectorAll('.gk-faq details, .faq details, .accordion details')].forEach(details => {
      if (details.dataset.mirthlyFaqBound === 'true') return;
      details.dataset.mirthlyFaq = 'true';
      details.dataset.mirthlyFaqBound = 'true';
      if (details.open) details.classList.add('is-motion-open');
      const summary = details.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', event => {
        if (reducedMotion.matches) return;
        event.preventDefault();
        if (details.open) {
          details.classList.remove('is-motion-open');
          window.setTimeout(() => { details.open = false; }, 300);
        } else {
          details.open = true;
          requestAnimationFrame(() => details.classList.add('is-motion-open'));
        }
      });
    });
  };

  const initButtons = root => {
    [...root.querySelectorAll('form[action*="/cart/add"]')].forEach(form => {
      if (form.dataset.mirthlyMotionBound === 'true') return;
      form.dataset.mirthlyMotionBound = 'true';
      form.addEventListener('submit', () => {
        const button = form.querySelector('[data-atc], [data-live-add], button[name="add"], .product-form__submit');
        if (!button || button.disabled) return;
        button.classList.add('is-loading');
        window.setTimeout(() => button.classList.remove('is-loading'), 1800);
      });
    });
  };

  const init = (root = document) => {
    setHeroMotion(root);
    setScrollMotion(root);
    observeMotion(root);
    initGallery(root);
    initSticky(root);
    initFaq(root);
    initButtons(root);
  };

  const boot = () => requestAnimationFrame(() => requestAnimationFrame(() => {
    initHeader(document);
    init(document);
  }));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
})();


